/**
 * Font Preloader Utility
 * Asynchronously preloads all text tattoo fonts to improve user experience
 */

export interface FontPreloadOptions {
  onProgress?: (loaded: number, total: number) => void;
  onComplete?: () => void;
  onError?: (fontName: string, error: Error) => void;
}

export class FontPreloader {
  private static instance: FontPreloader;
  private loadedFonts = new Set<string>();
  private isPreloading = false;
  private preloadingPromise: Promise<void> | null = null;

  // Font list must match the AVAILABLE_FONTS in EditorRefactored.tsx
  private readonly FONT_FAMILIES = [
    'Allura',
    'Dancing Script',
    'Dela Gothic One',
    'Dot Gothic 16',
    'Great Vibes',
    'Kaisei Decol',
    'M PLUS 1',
    'Mochiy Pop P One',
    'Noto Serif JP',
    'Reggae One',
    'RocknRoll One',
    'Sawarabi Mincho',
    'Shippori Mincho B1',
    'Stick',
    'Tangerine',
    'Train One',
    'Yusei Magic',
    'Zen Kaku Gothic New',
    'Zen Kurenaido',
    'Zen Old Mincho'
  ];

  public static getInstance(): FontPreloader {
    if (!FontPreloader.instance) {
      FontPreloader.instance = new FontPreloader();
    }
    return FontPreloader.instance;
  }

  /**
   * Check if all fonts are preloaded
   */
  public areAllFontsLoaded(): boolean {
    return this.loadedFonts.size === this.FONT_FAMILIES.length;
  }

  /**
   * Check if a specific font is loaded
   */
  public isFontLoaded(fontFamily: string): boolean {
    return this.loadedFonts.has(fontFamily);
  }

  /**
   * Get the current loading progress
   */
  public getProgress(): { loaded: number; total: number; percentage: number } {
    const loaded = this.loadedFonts.size;
    const total = this.FONT_FAMILIES.length;
    const percentage = Math.round((loaded / total) * 100);
    return { loaded, total, percentage };
  }

  /**
   * Preload all fonts asynchronously
   */
  public async preloadAllFonts(options: FontPreloadOptions = {}): Promise<void> {
    // If already preloading, return the existing promise
    if (this.preloadingPromise) {
      return this.preloadingPromise;
    }

    // If all fonts are already loaded, resolve immediately
    if (this.areAllFontsLoaded()) {
      options.onComplete?.();
      return Promise.resolve();
    }

    this.isPreloading = true;
    
    this.preloadingPromise = this.performPreloading(options);
    
    try {
      await this.preloadingPromise;
    } finally {
      this.isPreloading = false;
      this.preloadingPromise = null;
    }
  }

  private async performPreloading(options: FontPreloadOptions): Promise<void> {
    const { onProgress, onComplete, onError } = options;

    
    // Create promises for each font
    const fontPromises = this.FONT_FAMILIES.map(async (fontFamily) => {
      if (this.loadedFonts.has(fontFamily)) {
        return; // Already loaded
      }

      try {
        // Use document.fonts.load to preload the font
        // This will trigger the download of the font file
        await document.fonts.load(`1em "${fontFamily}"`);
        
        // Mark as loaded
        this.loadedFonts.add(fontFamily);

        
        // Report progress
        if (onProgress) {
          const progress = this.getProgress();
          onProgress(progress.loaded, progress.total);
        }
        
      } catch (error) {
        console.error(`FontPreloader: Failed to load font ${fontFamily}:`, error);
        if (onError) {
          onError(fontFamily, error as Error);
        }
        // Don't add to loadedFonts set if failed to load
      }
    });

    // Wait for all fonts to load (or fail)
    await Promise.allSettled(fontPromises);
    
    if (onComplete) {
      onComplete();
    }
  }

  /**
   * Preload a single font if not already loaded
   */
  public async preloadFont(fontFamily: string): Promise<boolean> {
    if (this.loadedFonts.has(fontFamily)) {
      return true; // Already loaded
    }

    if (!this.FONT_FAMILIES.includes(fontFamily)) {
      console.warn(`FontPreloader: Font "${fontFamily}" is not in the preload list`);
      return false;
    }

    try {
      await document.fonts.load(`1em "${fontFamily}"`);
      this.loadedFonts.add(fontFamily);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get the list of all font families
   */
  public getFontFamilies(): string[] {
    return [...this.FONT_FAMILIES];
  }

  /**
   * Reset the preloader state (mainly for testing)
   */
  public reset(): void {
    this.loadedFonts.clear();
    this.isPreloading = false;
    this.preloadingPromise = null;
  }
}

// Export a singleton instance for easy use
export const fontPreloader = FontPreloader.getInstance();

// Also export as default for better compatibility
export default fontPreloader;

// Export convenience functions
export const preloadAllFonts = (options?: FontPreloadOptions) => 
  fontPreloader.preloadAllFonts(options);

export const isFontLoaded = (fontFamily: string) => 
  fontPreloader.isFontLoaded(fontFamily);

export const areAllFontsLoaded = () => 
  fontPreloader.areAllFontsLoaded();

export const getFontLoadProgress = () => 
  fontPreloader.getProgress();